
import { Component, Vue, Watch } from 'vue-property-decorator'
import { embedApi, EmbedRequestError, InstrumentFilters } from './api'
import { embedAuth } from './auth'
import { formData, InstrumentData, Option } from './fields'
import InstrumentForm from './components/InstrumentForm.vue'
import InstrumentDetail from './components/InstrumentDetail.vue'

@Component({ components: { InstrumentForm, InstrumentDetail } })
export default class TempEmbedInstruments extends Vue {
  ready = false
  initializing = false
  accessError = ''
  listError = ''
  keyword = ''
  appliedKeyword = ''
  filters: InstrumentFilters = {}
  appliedFilters: InstrumentFilters = {}
  page = 1
  perPage = 20
  total = 0
  items: InstrumentData[] = []
  persons: Option[] = []
  departments: Option[] = []
  classifications: string[] = []
  filterClassifications: string[] = []
  assetNatures: string[] = []
  loading = false
  saving = false
  opening = false
  deleting = false
  dialogVisible = false
  mode: 'create' | 'edit' | 'detail' = 'detail'
  record: InstrumentData = {}
  draft: InstrumentData = formData()
  generation = 0
  listRequest = 0

  get accessToken() { return embedAuth.token }
  get busy() { return this.saving || this.opening || this.deleting }
  get dialogTitle() { return { create: '新增仪器', edit: '编辑仪器', detail: '仪器详情' }[this.mode] }

  mounted() { this.initialize() }

  beforeDestroy() { this.generation++; this.listRequest++ }

  @Watch('accessToken')
  onTokenChanged() { this.initialize() }

  message(error: unknown): string {
    return error instanceof Error ? error.message : '操作失败，请稍后重试'
  }

  handleError(error: unknown) {
    if (error instanceof EmbedRequestError && [401, 403].includes(error.status)) {
      this.ready = false
      this.dialogVisible = false
      this.items = []
      this.record = {}
      this.draft = formData()
      this.accessError = this.message(error)
    } else {
      this.$message.error(this.message(error))
    }
  }

  async initialize() {
    const generation = ++this.generation
    this.listRequest++
    this.ready = false
    this.dialogVisible = false
    this.items = []
    this.record = {}
    this.draft = formData()
    this.persons = []
    this.departments = []
    this.classifications = []
    this.filterClassifications = []
    this.assetNatures = []
    this.page = 1
    this.accessError = ''
    this.initializing = false
    if (!this.accessToken) {
      this.accessError = '缺少访问凭证，请使用完整的访问地址打开仪器台账'
      return
    }
    this.initializing = true
    try {
      const [persons, departments, options] = await Promise.all([
        embedApi.persons(), embedApi.departments(), embedApi.options()
      ])
      if (generation !== this.generation) return
      this.persons = persons
      this.departments = departments
      this.classifications = options.device_classifications
      this.assetNatures = options.asset_natures
      this.ready = true
      await this.loadList()
    } catch (error) {
      if (generation === this.generation) this.accessError = this.message(error)
    } finally {
      if (generation === this.generation) this.initializing = false
    }
  }

  async loadList() {
    const request = ++this.listRequest
    this.loading = true
    this.listError = ''
    try {
      const result = await embedApi.list(this.page, this.perPage, this.appliedKeyword, this.appliedFilters)
      if (request !== this.listRequest) return
      this.total = result.total
      this.filterClassifications = result.device_classifications
      if (this.page > 1 && (this.page - 1) * this.perPage >= result.total) {
        this.page = Math.max(1, Math.ceil(result.total / this.perPage))
        await this.loadList()
        return
      }
      this.items = result.items
    } catch (error) {
      if (request !== this.listRequest) return
      this.items = []
      this.total = 0
      this.listError = this.message(error)
      this.handleError(error)
    } finally {
      if (request === this.listRequest) this.loading = false
    }
  }

  search() {
    if (this.busy) return
    this.appliedKeyword = this.keyword.trim()
    this.appliedFilters = { ...this.filters }
    this.page = 1
    this.loadList()
  }

  resetSearch() { this.keyword = ''; this.filters = {}; this.search() }
  changePage(page: number) { this.page = page; this.loadList() }
  changeSize(size: number) { this.perPage = size; this.page = 1; this.loadList() }

  openCreate() {
    this.mode = 'create'
    this.record = {}
    this.draft = formData()
    this.dialogVisible = true
  }

  async openRecord(id: number, mode: 'detail' | 'edit') {
    const generation = this.generation
    this.opening = true
    try {
      const record = await embedApi.detail(id)
      if (generation !== this.generation || !this.ready) return
      this.record = record
      this.draft = formData(record)
      this.mode = mode
      this.dialogVisible = true
    } catch (error) {
      if (generation === this.generation) this.handleError(error)
    } finally { this.opening = false }
  }

  beforeClose(done: () => void) { if (!this.saving) done() }

  async save() {
    if (this.saving) return
    const generation = this.generation
    this.saving = true
    try {
      let payload = this.draft
      if (this.mode === 'edit') {
        // Keep untouched historical values intact, including nulls and old references.
        const original = formData(this.record)
        payload = {}
        Object.keys(this.draft).forEach(key => {
          if (this.draft[key] !== original[key]) payload[key] = this.draft[key]
        })
        if (!Object.keys(payload).length) {
          this.dialogVisible = false
          return
        }
      }
      const valid = await (this.$refs.instrumentForm as InstrumentForm).validate(
        this.mode === 'edit' ? Object.keys(payload) : undefined
      )
      if (!valid || generation !== this.generation || !this.ready) return
      if (this.mode === 'create') await embedApi.create(payload)
      else await embedApi.update(this.record.id, payload)
      if (generation !== this.generation) return
      this.dialogVisible = false
      this.$message.success('仪器已保存')
      await this.loadList()
    } catch (error) {
      if (generation === this.generation) this.handleError(error)
    } finally { this.saving = false }
  }

  async remove(row: InstrumentData) {
    const generation = this.generation
    this.deleting = true
    try {
      await this.$confirm(`确定删除“${row.name}”（${row.code}）吗？`, '删除仪器', {
        confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning'
      })
      if (generation !== this.generation || !this.ready) return
      await embedApi.remove(row.id)
      if (generation !== this.generation) return
      this.$message.success('仪器已删除')
      await this.loadList()
    } catch (error) {
      if (error !== 'cancel' && error !== 'close' && generation === this.generation) this.handleError(error)
    } finally { this.deleting = false }
  }
}
